"use client"

import { useEffect, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"
import styles from "./articles.module.css"

type Section = { id: string; navLabel: string }
export function ArticleNav({ sections }: { sections: readonly Section[] }) {
  const [active, setActive] = useState("")
  const disclosure = useRef<HTMLDetailsElement>(null)
  const trigger = useRef<HTMLElement>(null)
  useEffect(() => {
    const targets = sections.map(({ id }) => document.getElementById(id)).filter((el): el is HTMLElement => !!el)
    const headingFor = (element: HTMLElement) => element.querySelector<HTMLElement>("h2, h3") ?? element
    const focusHash = () => {
      let id: string
      try { id = decodeURIComponent(window.location.hash.slice(1)) } catch { return }
      const target = targets.find(el => el.id === id)
      if (!target) return
      setActive(id)
      if (disclosure.current) disclosure.current.open = false
      const heading = headingFor(target)
      heading.tabIndex = -1
      heading.focus({ preventScroll: true })
    }
    const updateActive = () => {
      const preceding = targets.filter(el => el.getBoundingClientRect().top <= 120)
      setActive((preceding.at(-1) ?? targets[0])?.id ?? "")
    }
    focusHash()
    updateActive()
    window.addEventListener("hashchange", focusHash)
    window.addEventListener("popstate", focusHash)
    window.addEventListener("scroll", updateActive, { passive: true })
    return () => {
      window.removeEventListener("hashchange", focusHash)
      window.removeEventListener("popstate", focusHash)
      window.removeEventListener("scroll", updateActive)
    }
  }, [sections])
  const links = <ol>{sections.map(({ id, navLabel }) => <li key={id}><a href={`#${id}`} aria-current={active === id ? "location" : undefined} onClick={() => {
    if (disclosure.current) disclosure.current.open = false
    // Keep native hash/history semantics, including repeat selection of the current hash.
    requestAnimationFrame(() => {
      const target = document.getElementById(id)
      const heading = target?.querySelector<HTMLElement>("h2, h3") ?? target
      if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }) }
      setActive(id)
    })
  }}>{navLabel}</a></li>)}</ol>
  return <>
    <nav className={styles.desktopNav} aria-label="On this page"><p>On this page</p>{links}</nav>
    <details ref={disclosure} className={styles.mobileNav} onKeyDown={event => {
      if (event.key === "Escape" && disclosure.current?.open) {
        event.preventDefault()
        disclosure.current.open = false
        trigger.current?.focus()
      }
    }}><summary ref={trigger}>On this page<ChevronDown aria-hidden="true" /></summary><nav aria-label="On this page">{links}</nav></details>
  </>
}
